import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import CmsSwitcherBar from "@/components/app/CmsSwitcherBar";
import OrphanAliasEditor from "@/components/admin/OrphanAliasEditor";
import ScrapflyAlertBanner from "@/components/admin/ScrapflyAlertBanner";
import { ArchiveConnectivityTest } from "@/components/admin/ArchiveConnectivityTest";
import {
  ShieldCheck,
  ClipboardList,
  Building2,
  MessageSquare,
  Link as LinkIcon,
  Loader2,
  ShieldAlert,
  ArrowRight,
  Sparkles,
  BookMarked,
  Inbox,
  Globe,
  Target,
  CalendarClock,
  BarChart3,
  Activity,
  Pencil,
  Trophy,
  KeyRound,
} from "lucide-react";

/**
 * /admin — branded CMS entrypoint.
 *
 * Not a separate auth system: reuses the same Google OAuth as regular
 * users and checks `/api/admin/whoami` to see if the current session's
 * email is on the `ADMIN_EMAILS` allowlist. Signed-out users see a "Sign
 * in" CTA; non-admins get an access-denied panel; admins get the tile
 * grid to every CMS surface.
 */
const TILES = [
  {
    key: "edit-any",
    to: "/admin/edit",
    icon: Pencil,
    title: "Edit any course or uni",
    body:
      "Search for any degree or university and open the registry-driven field editor. Every save writes an audit row.",
  },
  {
    key: "data-gap-reports",
    to: "/admin/data-gap-reports",
    icon: MessageSquare,
    title: "Family feedback inbox",
    body:
      "\"Help us improve\" reports from every empty course-page cell. Apply the fix in one click and mark actioned.",
  },
  {
    key: "uni-verification",
    to: "/admin/uni-verification",
    icon: Trophy,
    title: "Uni verification leaderboard",
    body:
      "Rank claimed universities by unique fields verified. Amber rows flag cold claims that need chasing.",
  },
  {
    key: "verify",
    to: "/admin/verify",
    icon: ShieldCheck,
    title: "Evidence Verification",
    body:
      "Researcher queue: verify tuition, offer rates, and entry requirements against official sources.",
  },
  {
    key: "claims",
    to: "/admin/claims",
    icon: Building2,
    title: "University Claims",
    body:
      "Approve or reject university-ownership claims that failed the auto-domain check.",
  },
  {
    key: "firm-portal",
    to: "/admin/firm-portal",
    icon: CalendarClock,
    title: "Firm-Portal Moderation",
    body:
      "Approve or reject firm-submitted events, alumni-mentors, and profile edits before they go live on public pages.",
  },
  {
    key: "impersonate",
    to: "/admin/impersonate",
    icon: KeyRound,
    title: "Impersonation",
    body:
      "Mint a single-use magic-link URL for any account (test uni owner, firm owner, QA user). Every mint is audit-logged.",
  },
  {
    key: "featured-cities",
    to: "/admin/featured-cities",
    icon: Sparkles,
    title: "Featured Cities",
    body: "Pick and order the cities, subjects and firms that rotate on the homepage, and approve student quotes collected from Reddit.",
  },
  {
    key: "reviews",
    to: "/admin/reviews",
    icon: MessageSquare,
    title: "Review Moderation",
    body:
      "Approve or reject student-satisfaction reviews before they appear publicly.",
  },
  {
    key: "surveys",
    to: "/admin/surveys/invites",
    icon: LinkIcon,
    title: "Survey Invites",
    body: "Generate shareable degree-survey links and track their click / submission counts.",
  },
  {
    key: "batch",
    to: "/admin/ai-fetch-batch",
    icon: Sparkles,
    title: "Batch AI-Fetch",
    body:
      "Kick off a country-wide assisted-verification run. Suggestions land in /admin/verify — you still review and Save.",
  },
  {
    key: "ucas",
    to: "/admin/ucas",
    icon: BookMarked,
    title: "UCAS Fix Queue",
    body:
      "Approve UCAS-suggested course codes for UK degrees that still need live verification — pre-ranked by fastest wins.",
  },
  {
    key: "entry-tariff",
    to: "/admin/entry-tariff",
    icon: Sparkles,
    title: "Entry-Tariff Queue",
    body:
      "AI-proposed entry gates (audition/portfolio/grades) — approve to write UCAS tariff + gate to every degree at that uni.",
  },
  {
    key: "course-fit-governance",
    to: "/admin/course-fit/governance",
    icon: ShieldCheck,
    title: "Fast Match — Release Governance",
    body:
      "Release status, integrity checks, beta feature-flag, activation/retirement, and Stage-05 operational metrics.",
  },
  {
    key: "leads",
    to: "/admin/leads",
    icon: Inbox,
    title: "Leads Triage",
    body:
      "Every WUA+ specialist / Pearsana lead — email families and mark contacted, matched, or dismissed until real dispatch lands.",
  },
  {
    key: "coverage-health",
    to: "/admin/coverage-health",
    icon: Globe,
    title: "Coverage Health",
    body:
      "Degree counts and editorial fill-rates per country — surfaces the weakest cell so the next coverage wave hits the biggest gap.",
  },
  {
    key: "coverage-benchmarks",
    to: "/admin/coverage-benchmarks",
    icon: Target,
    title: "Coverage Benchmarks",
    body:
      "Live audit of universities + degrees vs canonical target universes per country. Highlights the Recommended Next country on the way to complete coverage.",
  },
  {
    key: "editorial-calendar",
    to: "/admin/calendar",
    icon: CalendarClock,
    title: "Editorial Calendar",
    body:
      "What to do this week, next 30 days, and later — watch feeds, verification windows, editorial moments, and manual-CSV sources (HESA, QILT, NSSE, HEA) with source URLs, expected filenames and a one-tap upload + auto-seed workflow.",
  },
  {
    key: "salary-coverage",
    to: "/admin/salary-coverage",
    icon: BarChart3,
    title: "Salary Source Coverage",
    body:
      "Per-country % of universities carrying a verified salary source from a recognised national statistics body (HESA, QILT, Scorecard, CBS, Céreq, StatCan…). Weakest countries surface at the top so you can point the next per-uni salary uplift wave.",
  },
  {
    key: "progress-dash",
    to: "/admin/progress",
    icon: Activity,
    title: "Progress Dash",
    body:
      "Every backfill, daemon, and AI-content coverage tile in one place — editorial reviews, cluster-5 narratives, course tips, US deadlines, scholarships, offer-rate, professor regen, Scrapfly usage, URL rot.",
  },
];

export default function AdminHome() {
  const { user, loading, login } = useAuth();
  const [state, setState] = useState({ checking: true, isAdmin: false });
  const [counts, setCounts] = useState({ pending_claims: null, pending_reviews: null, unverified_editorial: null });

  useEffect(() => {
    if (loading) return;
    if (!user) {
      setState({ checking: false, isAdmin: false });
      return;
    }
    (async () => {
      try {
        await api.get("/admin/whoami");
        setState({ checking: false, isAdmin: true });
        // Best-effort counts for the tile subtitles.
        const [c, r, s] = await Promise.all([
          api.get("/admin/claims/pending").catch(() => ({ data: { total: null } })),
          api.get("/admin/reviews/pending").catch(() => ({ data: { items: [] } })),
          api.get("/admin/verify/stats").catch(() => ({ data: null })),
        ]);
        setCounts({
          pending_claims: c.data?.total ?? (c.data?.items?.length ?? null),
          pending_reviews: r.data?.items?.length ?? null,
          unverified_editorial:
            s.data?.editorial_total != null && s.data?.editorial_verified != null
              ? s.data.editorial_total - s.data.editorial_verified
              : null,
        });
      } catch {
        setState({ checking: false, isAdmin: false });
      }
    })();
  }, [user, loading]);

  if (loading || state.checking) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-24 text-stone-500 font-mono text-sm" data-testid="admin-home-loading">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
        Loading the newsroom…
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24" data-testid="admin-home-signin">
        <div className="p-10 border border-stone-300 rounded-xl bg-white">
          <div className="inline-flex items-center gap-2 text-[10px] font-underground-sc tracking-widest text-stone-500">
            <ShieldCheck className="h-3 w-3" /> Editorial CMS
          </div>
          <h1 className="mt-3 font-editorial text-4xl leading-tight">
            The Which University Advisor newsroom.
          </h1>
          <p className="mt-3 text-stone-600 text-base leading-relaxed">
            Sign in with your editorial Google account to open the CMS. Access is
            restricted to accounts on the admin allowlist.
          </p>
          <Button
            className="mt-6"
            data-testid="admin-home-signin-btn"
            onClick={login}
          >
            Sign in with Google <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
          <p className="mt-4 text-[11px] font-underground-sc tracking-wider text-stone-400">
            Non-admin sign-ins are automatically redirected back to the shortlist.
          </p>
        </div>
      </div>
    );
  }

  if (!state.isAdmin) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16" data-testid="admin-home-forbidden">
        <div className="p-8 border border-stone-300 rounded-lg bg-white">
          <ShieldAlert className="h-8 w-8 text-stone-500" />
          <h1 className="mt-3 font-editorial text-2xl">Not on the editorial allowlist</h1>
          <p className="mt-2 text-stone-600 text-sm">
            You are signed in as <span className="font-mono">{user.email}</span>, but
            this email is not on the admin allowlist (<span className="font-mono">ADMIN_EMAILS</span>).
            If you should have access, ask a fellow editor to add you.
          </p>
          <div className="mt-4 flex gap-3">
            <Link to="/">
              <Button variant="outline" size="sm">Back to site</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <CmsSwitcherBar />
      <div className="max-w-6xl mx-auto px-6 py-12" data-testid="admin-home-page">
      <ScrapflyAlertBanner />
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-underground-sc tracking-widest text-stone-500">
            Editorial CMS
          </div>
          <h1 className="mt-1 font-editorial text-4xl">Newsroom</h1>
          <p className="mt-2 text-stone-600 text-sm">
            Signed in as <span className="font-mono">{user.email}</span>.
          </p>
        </div>
        <div className="text-[11px] font-mono text-stone-500 uppercase tracking-wider">
          {counts.unverified_editorial != null && (
            <span className="mr-4" data-testid="admin-home-count-unverified">
              {counts.unverified_editorial} editorial degrees to verify
            </span>
          )}
          {counts.pending_claims != null && (
            <span className="mr-4" data-testid="admin-home-count-claims">
              {counts.pending_claims} pending claims
            </span>
          )}
          {counts.pending_reviews != null && (
            <span data-testid="admin-home-count-reviews">
              {counts.pending_reviews} pending reviews
            </span>
          )}
        </div>
      </div>

      <ArchiveConnectivityTest />

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
        {TILES.map((t) => {
          const Icon = t.icon;
          const badge =
            (t.key === "claims" && counts.pending_claims) ||
            (t.key === "reviews" && counts.pending_reviews) ||
            (t.key === "verify" && counts.unverified_editorial) ||
            null;
          return (
            <Link
              key={t.key}
              to={t.to}
              data-testid={`admin-home-tile-${t.key}`}
              className="block p-6 border border-stone-300 rounded-xl bg-white hover:border-stone-900 transition-colors group"
            >
              <div className="flex items-start justify-between">
                <Icon className="h-6 w-6 text-stone-800" />
                {badge ? (
                  <span className="text-[10px] font-underground-sc tracking-wider bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5">
                    {badge} open
                  </span>
                ) : null}
              </div>
              <h2 className="mt-3 font-editorial text-xl">{t.title}</h2>
              <p className="mt-2 text-sm text-stone-600 leading-relaxed">{t.body}</p>
              <div className="mt-4 text-[11px] font-underground-sc tracking-wider text-stone-500 flex items-center gap-1 group-hover:text-stone-900">
                Open <ArrowRight className="h-3 w-3" />
              </div>
            </Link>
          );
        })}
      </div>

      <div className="mt-8">
        <OrphanAliasEditor />
      </div>

      <div className="mt-10 p-5 border border-dashed border-stone-300 rounded-xl bg-stone-50/50">
        <div className="flex items-start gap-3">
          <ClipboardList className="h-5 w-5 text-stone-600 mt-0.5" />
          <div>
            <div className="text-sm text-stone-800 font-semibold">
              Ownership &amp; provenance
            </div>
            <p className="mt-1 text-xs text-stone-600 leading-relaxed max-w-2xl">
              University staff can claim their institution via <Link to="/uni-portal" className="underline">the University Portal</Link>.
              Domain-matched sign-ins are auto-approved; everyone else lands in the
              Claims queue above. All edits stamp a "last updated by uni" line on the
              affected fields.
            </p>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
